import React, { useEffect, useRef, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  AtSymbolIcon,
  ClipboardDocumentListIcon,
  BookOpenIcon,
  TrophyIcon,
  ArrowRightOnRectangleIcon,
  SunIcon,
  MoonIcon,
  IdentificationIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../services/api';
import { WingrLockup } from '../components/WingrMascot';

const VA_NAV = [
  { name: 'Overview', href: '/va', icon: HomeIcon, end: true },
  { name: 'Leaderboard', href: '/va/leaderboard', icon: TrophyIcon },
  { name: 'Accounts', href: '/va/accounts', icon: ClipboardDocumentListIcon },
  { name: 'Target Usernames', href: '/va/targets', icon: AtSymbolIcon },
  { name: 'Guides', href: '/va/guides', icon: BookOpenIcon }
];

function isNavActive(item, pathname) {
  if (item.end) return pathname === item.href;
  return pathname.startsWith(item.href);
}

function navLinkClass(active, isDarkMode) {
  if (active) {
    return isDarkMode
      ? 'bg-gradient-to-r from-blue-500/20 to-sky-500/20 text-white border border-blue-500/30'
      : 'bg-gradient-to-r from-blue-500/10 to-sky-500/10 text-blue-700 border border-blue-200';
  }
  return isDarkMode
    ? 'text-gray-300 hover:bg-white/10 hover:text-white'
    : 'text-gray-600 hover:bg-white/60 hover:text-gray-900';
}

const VALayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, isAuthenticated, loading } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const lastPathRef = useRef('');

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!loading && user && user.role !== 'va' && user.role !== 'admin') {
      navigate('/dashboard', { replace: true });
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    if (loading || !user || user.role !== 'va') return undefined;

    const sendHeartbeat = (navigation) => {
      api.post('/va/activity/heartbeat', {
        path: location.pathname,
        navigation
      }).catch(() => {});
    };

    const isNavigation = lastPathRef.current !== location.pathname;
    lastPathRef.current = location.pathname;
    sendHeartbeat(isNavigation);

    const interval = window.setInterval(() => sendHeartbeat(false), 60000);
    return () => window.clearInterval(interval);
  }, [loading, user, location.pathname]);

  const isAdminPreview = user?.role === 'admin';
  const displayName = user?.vaProfile?.displayName || user?.name || user?.username;
  const activeNav = VA_NAV.find((item) => isNavActive(item, location.pathname)) || VA_NAV[0];

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  const renderNavLinks = (onNavigate) =>
    VA_NAV.map((item) => {
      const active = isNavActive(item, location.pathname);
      return (
        <Link
          key={item.href}
          to={item.href}
          onClick={onNavigate}
          className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-all duration-200 ${navLinkClass(active, isDarkMode)}`}
        >
          <item.icon className="mr-3 h-5 w-5" />
          {item.name}
        </Link>
      );
    });

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 glass-overlay" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex w-64 flex-col glass-sidebar">
          <div className="flex h-20 items-center justify-between px-4">
            <div className="flex items-center">
              <WingrLockup size={32} />
            </div>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="px-4 pb-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-400">Virtual Assistant</div>
            <div className="mt-1 text-sm font-medium text-gray-700 dark:text-gray-200 truncate">{displayName}</div>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {renderNavLinks(() => setSidebarOpen(false))}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow glass-sidebar">
          <div className="flex h-20 items-center px-4">
            <div className="flex items-center">
              <WingrLockup size={32} />
            </div>
          </div>
          <div className="px-4 pb-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-400">Virtual Assistant</div>
            <div className="mt-1 text-sm font-medium text-gray-700 dark:text-gray-200 truncate">{displayName}</div>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4 overflow-y-auto">
            {renderNavLinks()}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 glass-nav px-4 sm:gap-x-6 sm:px-6 lg:px-8 border-b border-gray-200/50 dark:border-gray-700/50">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 dark:text-gray-300 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="ml-auto flex items-center gap-4">
            {isAdminPreview && (
              <Link
                to="/dashboard/admin/vas"
                className="hidden sm:inline-flex text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                VA management
              </Link>
            )}
            <span className="text-sm text-gray-600 dark:text-gray-300 truncate max-w-[160px] sm:max-w-none">
              {displayName}
              {isAdminPreview && (
                <span className="ml-1.5 text-xs text-indigo-600 dark:text-indigo-400">(preview)</span>
              )}
            </span>
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-1 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              title="Sign out"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              <span className="hidden sm:inline">Logout</span>
            </button>
            <button
              type="button"
              onClick={toggleTheme}
              className="p-2 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <main className="py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-6">
            {/* Hero — matches /dashboard/admin/vas */}
            <div className={`relative overflow-hidden rounded-2xl ${
              isDarkMode
                ? 'bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900'
                : 'bg-gradient-to-br from-indigo-50 via-violet-50 to-slate-50'
            }`}>
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className={`absolute -top-20 right-1/4 w-72 h-72 rounded-full blur-3xl ${
                  isDarkMode ? 'bg-indigo-600/20' : 'bg-indigo-300/40'
                }`} />
                <div className={`absolute -bottom-16 left-1/4 w-64 h-64 rounded-full blur-3xl ${
                  isDarkMode ? 'bg-violet-600/15' : 'bg-violet-200/50'
                }`} />
              </div>
              <div className="relative px-6 sm:px-8 py-8 sm:py-10">
                <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                  <div className="flex items-start gap-5 flex-1 min-w-0">
                    <div className={`flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-2xl ${
                      isDarkMode
                        ? 'bg-gradient-to-br from-indigo-500 to-violet-600 shadow-lg shadow-indigo-500/30'
                        : 'bg-gradient-to-br from-indigo-500 to-violet-500 shadow-lg shadow-indigo-400/30'
                    }`}>
                      <IdentificationIcon className="w-7 h-7 text-white" />
                    </div>
                    <div className="min-w-0">
                      <h1 className={`text-2xl sm:text-3xl font-bold tracking-tight ${
                        isDarkMode ? 'text-white' : 'text-slate-900'
                      }`}>
                        {activeNav.name}
                      </h1>
                      <p className={`mt-1.5 text-sm sm:text-base ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-600'
                      }`}>
                        Virtual Assistant Portal — welcome, {displayName}.
                      </p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 hidden md:block">
                    <WingrLockup size={36} />
                  </div>
                </div>
              </div>
            </div>

            {isAdminPreview && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/40 px-4 py-3">
                <p className="text-sm text-indigo-900 dark:text-indigo-100">
                  <span className="font-semibold">Admin preview</span> — profile accounts require opening as a specific VA from VA management.
                </p>
                <Link
                  to="/dashboard/admin/vas"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-700 dark:text-indigo-300 hover:underline whitespace-nowrap"
                >
                  Back to VA management
                </Link>
              </div>
            )}

            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default VALayout;
